import { paidStatuses } from "@/src/constants/billingConstants.js";
import { bannerStatuses } from "@/src/constants/bannerConstants.js";
import { callDirections } from "@/src/constants/callConstants.js";
import { toFetchPhoneNumberDetails, getRecipientDetails } from "@/src/utils/callsUtils.js";
import { fetchCallDispositions } from "@/src/utils/callsUtils.js";

export default {
  namespaced: true,

  state: {
    outgoingCall: {
      isActive: false,
      details: {
        name: "",
        number: "",
        code: "",
        From: "",
        status: "",
      },
      contactDetails: null, 
    },
    incomingCall: {
      isActive: false,
      details: {
        name: "",
        number: "",
        From: "",
        status: "",
      },
      contactDetails: null,
    },
    currentCallId: null,
    currentCallNote: "",
    isCampaign: false,
    generatingLead: false,
    fetchingDetails: false,
    recordingConsentStatus: false,
    isCallConnected: false,
    entityDetails: null,
    showDial: false,
    showAddCreditModal: false,
    dispositions: [],
    dispositionDrawerOpen: false,
    pendingCampaignRedirect: null,
  },

  mutations: {
    SET_OUTGOING_CALL(state, { isActive, details = null }) {
      state.outgoingCall.isActive = isActive;
      if (details) {
        state.outgoingCall.details = details;
      } else {
        state.outgoingCall.details = {
          name: "",
          number: "",
          code: "",
          From: "",
          status: "",
        };
        state.outgoingCall.contactDetails = null;
      }
    },

    SET_INCOMING_CALL(state, { isActive, details = null }) {
      state.incomingCall.isActive = isActive;
      if (details) {
        state.incomingCall.details = details;
      } else {
        state.incomingCall.details = {
          name: "",
          number: "",
          From: "",
          status: "",
        };
        state.incomingCall.contactDetails = null;
      }
    },

    SET_CONTACT_DETAILS(state, { type, details }) {
      if (type === callDirections.OUTGOING) {
        state.outgoingCall.contactDetails = details;
      } else if (type === callDirections.INCOMING) {
        state.incomingCall.contactDetails = details;
      }
    },

    SET_INCOMING_CONTACT_DETAILS(state, details) {
      state.incomingCall.contactDetails = details;
    },

    UPDATE_CALL_STATUS(state, { status }) {
      if (state.outgoingCall.isActive) {
        state.outgoingCall.details.status = status;
      }
      if (state.incomingCall.isActive) {
        state.incomingCall.details.status = status;
      }
    },

    SET_CURRENT_CALL_ID(state, id) {
      state.currentCallId = id;
    },

    SET_CALL_NOTE(state, note) {
      state.currentCallNote = note;
    },

    SET_IS_CAMPAIGN(state, value) {
      state.isCampaign = value;
    },

    SET_GENERATING_LEAD(state, value) {
      state.generatingLead = value;
    },

    SET_IS_RECORDING_CONSENT_STATUS(state, value) {
      state.recordingConsentStatus = value;
    },

    SET_FETCHING_DETAILS(state, value) {
      state.fetchingDetails = value;
    },

    SET_IS_CALL_CONNECTED(state, value) {
      state.isCallConnected = value;
    },

    SET_ENTITY_DETAILS(state, value) {
      state.entityDetails = value;
    },

    SET_SHOW_DIAL(state, value) {
      state.showDial = value;
    },

    SET_ADD_CREDIT_MODAL(state, value) {
      state.showAddCreditModal = value;
    },

    SET_DISPOSITIONS(state, dispositions) {
      state.dispositions = dispositions;
    },

    SET_DISPOSITION_DRAWER_OPEN(state, value) {
      state.dispositionDrawerOpen = value;
    },

    SET_PENDING_CAMPAIGN_REDIRECT(state, value) {
      state.pendingCampaignRedirect = value;
    },

    RESET_STATE(state) {
      state.outgoingCall = {
        isActive: false,
        details: {
          name: "",
          number: "",
          code: "",
          From: "",
          status: "",
        },
        contactDetails: null,
      };
      state.incomingCall = {
        isActive: false,
        details: {
          name: "",
          number: "",
          From: "",
          status: "",
        },
        contactDetails: null,
      };
      state.currentCallId = null;
      state.isCampaign = false;
      state.fetchingDetails = false;
      state.isCallConnected = false;
      state.entityDetails = null;
    },
  },

  actions: {
    async startOutgoingCall(
      { commit, dispatch, state },
      { details, isCampaign = false },
    ) {
      commit("SET_OUTGOING_CALL", {
        isActive: true,
        details: { ...details, status: "connecting" },
      });
      commit("SET_IS_CAMPAIGN", isCampaign);
      if (state.entityDetails) {
        commit("SET_CONTACT_DETAILS", { 
          type: callDirections.OUTGOING, 
          details: state.entityDetails 
        });
        return;
      } 
      if (isCampaign) {
        return;
      }
      await dispatch("fetchContactDetails", {
        type: callDirections.OUTGOING,
        number: details.number,
      });
    },

    async startIncomingCall({ commit, dispatch }, details) {
      commit("SET_INCOMING_CALL", {
        isActive: true,
        details: { ...details, status: "pending" },
      });
      await dispatch("fetchContactDetails", {
        type: callDirections.INCOMING,
        number: details.From,
        entityId: details.entityId,
        entityType: details.entityType,
      });
    },

    async fetchContactDetails({ commit }, { type, number, entityId, entityType }) {
      try {
        commit("SET_FETCHING_DETAILS", true);
        let contactDetails = null;

        if (entityId && entityType) {
          contactDetails = await getRecipientDetails(entityId, entityType);
        }

        if (!contactDetails && number) {
          contactDetails = await toFetchPhoneNumberDetails(number);
        }

        if (contactDetails) {
          commit("SET_CONTACT_DETAILS", { type, details: contactDetails });
        }
      } catch (error) {
        console.error("Error fetching contact details:", error);
      } finally {
        commit("SET_FETCHING_DETAILS", false);
      }
    },

    async checkBillingAndShowCreditModal({ dispatch, commit, rootState }) {
      try {
        const accountData = await dispatch(
          "billing/toGetBillingAccount",
          null,
          { root: true },
        );
        const isPaid = paidStatuses.includes(accountData?.status);

        if (!isPaid) {
          dispatch("setShowAddCreditModal", true);
        }
      } catch (error) {
        console.error("Failed to check billing and show credit modal:", error);
      }
    },

    setCurrentCallId({ commit }, callId) {
      commit("SET_CURRENT_CALL_ID", callId);
    },

    setIncomingContactDetails({ commit }, details) {
      commit("SET_INCOMING_CONTACT_DETAILS", details);
    },

    endOutgoingCall({ commit }) {
      commit("SET_OUTGOING_CALL", { isActive: false });
      commit("SET_IS_CAMPAIGN", false);
      commit("SET_IS_CALL_CONNECTED", false);
      commit("SET_ENTITY_DETAILS", null);

    },

    endIncomingCall({ commit }) {
      commit("SET_INCOMING_CALL", { isActive: false });
      commit("SET_IS_CALL_CONNECTED", false); 
      commit("SET_ENTITY_DETAILS", null);
    },

    updateCallStatus({ commit, dispatch, rootState }, status) {
      try {
        commit("UPDATE_CALL_STATUS", { status });

        if (status === "open") {
          commit("SET_IS_CALL_CONNECTED", true);
          if (rootState?.callsCampaign?.callOngoing) {
            dispatch(
              "dynamicBanner/updateBannerStatus",
              bannerStatuses.CONNECTED,
              { root: true },
            );
          }
        } else {
          commit("SET_IS_CALL_CONNECTED", false);
          dispatch(
            "dynamicBanner/updateBannerStatus",
            bannerStatuses.IN_PROGRESS,
            {
              root: true,
            },
          );
        }

        if (rootState.callsCampaign.currentCampaign.isActive) {
          dispatch("callsCampaign/refreshCampaignDetails", null, {
            root: true,
          });
        }
      } catch (error) {
        console.error("An error occurred while updating call status: ", error);
      }
    },

    setCallNote({ commit }, note) {
      commit("SET_CALL_NOTE", note);
    },

    setIsCallConnected({ commit }, value) {
      commit("SET_IS_CALL_CONNECTED", value);
    },

    setIsCampaign({ commit }, value) {
      commit("SET_IS_CAMPAIGN", value);
    },

    setGeneratingLead({ commit }, value) {
      commit("SET_GENERATING_LEAD", value);
    },

    setRecordingConsentStatus({ commit }, status) {
      commit("SET_IS_RECORDING_CONSENT_STATUS", status);
    },

    setEntityDetails({ commit }, details) {
      commit("SET_ENTITY_DETAILS", details);
    },

    setContactDetails({ commit }, { type, details }) {
      commit("SET_CONTACT_DETAILS", { type, details });
    },

    setShowDial({ commit }, value) {
      commit("SET_SHOW_DIAL", value);
    },

    setShowAddCreditModal({ commit }, value) {
      const shouldShow = !!localStorage.getItem("phone_number_purchase")
        ? false
        : value;
      commit("SET_ADD_CREDIT_MODAL", shouldShow);
      return shouldShow;
    },

    async fetchDispositions({ commit }) {
      try {
        const dispositions = await fetchCallDispositions({
          page: 1,
          pageSize: 40,
        });
        if (dispositions && dispositions.length > 0) {
          commit("SET_DISPOSITIONS", dispositions);
        }
      } catch (error) {
        console.error("Error fetching dispositions", error);
      }
    },

    async refreshDispositions({ dispatch }) {
      await dispatch("fetchDispositions");
    },

    refresh({ commit }) {
      commit("RESET_STATE");
    },

    setDispositionDrawerOpen({ commit }, value) {
      commit("SET_DISPOSITION_DRAWER_OPEN", value);
    },

    setPendingCampaignRedirect({ commit }, value) {
      commit("SET_PENDING_CAMPAIGN_REDIRECT", value);
    },
  },

  getters: {
    outgoingCallActive: (state) => state.outgoingCall.isActive,
    outgoingCallDetails: (state) => state.outgoingCall.details,
    outgoingContactDetails: (state) => state.outgoingCall.contactDetails,
    incomingCallActive: (state) => state.incomingCall.isActive,
    incomingCallDetails: (state) => state.incomingCall.details,
    incomingContactDetails: (state) => state.incomingCall.contactDetails,
    currentCallNote: (state) => state.currentCallNote,
    isCampaign: (state) => state.isCampaign,
    fetchingDetails: (state) => state.fetchingDetails,
    generatingLead: (state) => state.generatingLead,
    recordingConsentStatus: (state) => state.recordingConsentStatus,
    hasActiveCall: (state) =>
      state.outgoingCall?.isActive || state.incomingCall.isActive,
    isCallConnected: (state) => state.isCallConnected,
    currentCallId: (state) => state.currentCallId,
    entityDetails: (state) => state.entityDetails,
    showDial: (state) => state.showDial,
    showAddCreditModal: (state) => state.showAddCreditModal,
    dispositions: (state) => state.dispositions,
    dispositionDrawerOpen: (state) => state.dispositionDrawerOpen,
    pendingCampaignRedirect: (state) => state.pendingCampaignRedirect,
  },
};
